/**
 * Adapted from https://github.com/adopted-ember-addons/active-model-adapter/blob/acfca74315c3349fb3e9a2803d17a4c64e31119e/addon/active-model-adapter.ts#L1
 *
 * At time of writing, the addon is a bit behind the times and holding us back. All we need from it is this file.
 */

import RESTAdapter from '@ember-data/adapter/rest'
import AdapterError from '@ember-data/adapter/error'
import { pluralize } from '@ember-data/request-utils/string'
import { decamelize, underscore } from '@ember/string'

interface ActiveModelPayload {
  errors?: Record<string, string>
}

export default class ActiveModelAdapter extends RESTAdapter {
  /**
   * The ActiveModelAdapter overrides the `pathForType` method to build
   * underscored URLs by decamelizing and pluralizing the object type name.
   */
  pathForType(modelName: string): string {
    const decamelized = decamelize(modelName)
    const underscored = underscore(decamelized)
    return pluralize(underscored)
  }

  /**
   * The ActiveModelAdapter overrides the `handleResponse` method
   * to format errors passed to a DS.InvalidError for all
   * 422 Unprocessable Entity responses.
   */
  handleResponse(...args: Parameters<RESTAdapter['handleResponse']>): any {
    const [status, headers, payload, requestData] = args
    if (this.isInvalid(status, headers, payload)) {
      const adapterPayload = payload as ActiveModelPayload
      const errors = errorsHashToArray(adapterPayload.errors)

      const err = new AdapterError(errors, 'The adapter rejected the commit because it was invalid')
      err.code = 'InvalidError'
      return err
    } else {
      return super.handleResponse(status, headers, payload, requestData)
    }
  }
}

interface ErrorObject {
  title: string
  detail: unknown
  source: {
    pointer: string
  }
}

const PRIMARY_ATTRIBUTE_KEY = 'base'
function errorsHashToArray(errors?: Record<string, string>) {
  const out: ErrorObject[] = []

  if (errors) {
    Object.keys(errors).forEach((key) => {
      const messages = makeArray(errors[key])
      for (let i = 0; i < messages.length; i++) {
        let title = 'Invalid Attribute'
        let pointer = `/data/attributes/${key}`
        if (key === PRIMARY_ATTRIBUTE_KEY) {
          title = 'Invalid Document'
          pointer = `/data`
        }
        out.push({
          title: title,
          detail: messages[i],
          source: {
            pointer: pointer,
          },
        })
      }
    })
  }

  return out
}

function makeArray(value: unknown) {
  return Array.isArray(value) ? value : [value]
}
